import _ from 'underscore';
import React, { useCallback, useState, useEffect, useMemo } from 'react';

import {
  FilterType,
  AllSupportedFilters,
  CSMLTimeFrame,
  FolderAssetType,
  getDateAddedChoiceOptions,
  civilDateRangeEqual,
  ROOT_FOLDER_ID,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useAdStudioFiltersReducer } from './use-filters-reducer';
import { FilterBarContainer } from '../filter-bar-container';

// always show a search text filter
/* istanbul ignore next */
const PERFORMANCE_PAGE_FILTER_BAR_INIT_STATE = [{
  ...AllSupportedFilters[FilterType.SearchText],
  id: 0,
  value: '',
  humanizedValue: '',
  isFilterAlwaysVisible: true, // always visible even no value set
}];
/* istanbul ignore next */
export const useFilterBar = ({
  onFilterValueChange, currentFolderId, dateFormat, ianaTimeZone, i18n, initialState = PERFORMANCE_PAGE_FILTER_BAR_INIT_STATE,
  isAdsStudioAIGCMVPEnabled,
}) => {
  const {
    filters, addNewFilter, removeFilter, updateFilter, removeAllFilters, removeEmptyFilters, overwriteFilters,
  } = useAdStudioFiltersReducer({ initialState, supportedFilterTypes: AllSupportedFilters });

  const [isEditingFilter, setIsEditingFilter] = useState(false);

  const isAnyFilterSet = _.some(filters, ({ value }) => {
    const {
      searchText, dateAddedFilter, minImageHeight, minImageWidth, importSource, searchLocationFilter, topRated: topRatedFilterChecked, assetSources,
    } = value || {};
    return searchText || importSource || searchLocationFilter || minImageHeight || minImageWidth
      || (dateAddedFilter && dateAddedFilter !== CSMLTimeFrame.Anytime) || topRatedFilterChecked || !_.isEmpty(assetSources);
  });

  const allSupportedFilters = useMemo(() => {
    const selectedFilters = { ...AllSupportedFilters };

    // for V4, only images are shown in the perf table,so hide these 2 filters
    delete selectedFilters[FilterType.AssetType];
    delete selectedFilters[FilterType.VideoDuration];

    if (isAdsStudioAIGCMVPEnabled) {
      delete selectedFilters[FilterType.ImportSource];
    } else {
      delete selectedFilters[FilterType.AssetSource];
    }
    return selectedFilters;
  }, [isAdsStudioAIGCMVPEnabled]);

  const renderFilterBar = useCallback((allFilters, isEditing) => (
    <FilterBarContainer
      filters={allFilters}
      addNewFilter={addNewFilter}
      updateFilter={updateFilter}
      removeFilter={removeFilter}
      removeAllFilters={removeAllFilters}
      removeEmptyFilters={removeEmptyFilters}
      allSupportedFilters={allSupportedFilters}
      isEditingFilter={isEditing}
      setIsEditingFilter={setIsEditingFilter}
      onFilterValueChange={onFilterValueChange}
      i18n={i18n}
      ianaTimeZone={ianaTimeZone}
      dateFormat={dateFormat}
      isAdsStudioAIGCMVPEnabled={isAdsStudioAIGCMVPEnabled}
    />
  ), [addNewFilter, allSupportedFilters, dateFormat, i18n, ianaTimeZone, isAdsStudioAIGCMVPEnabled, onFilterValueChange, removeAllFilters, removeEmptyFilters, removeFilter, updateFilter]);

  const [effectiveFilters, setEffectiveFilters] = useState({});

  useEffect(() => {
    let isDateChanged = false;
    let filterValues = filters.reduce((acc, { type, value }) => {
      // transform the filters if needed
      if (type === FilterType.DateAdded) {
        const { dateAddedFilter, dateAddedFilterCustomDateRange } = value || {};
        let dateAddedDateRange;
        if (dateAddedFilter === CSMLTimeFrame.Custom) {
          dateAddedDateRange = dateAddedFilterCustomDateRange;
        } else {
          const dateAddedChoiceOptions = getDateAddedChoiceOptions(i18n, ianaTimeZone);
          const { range } = _.find(dateAddedChoiceOptions, o => o.key === dateAddedFilter) || {};
          dateAddedDateRange = range;
        }
        if (dateAddedDateRange && (dateAddedDateRange.start || dateAddedDateRange.end)) {
          return { ...acc, dateAddedDateRange };
        }
        return acc;
      }
      return value ? { ...acc, ...value } : acc;
    }, { isGlobalSearch: currentFolderId === ROOT_FOLDER_ID });
    // remove empty values
    filterValues = _.pick(filterValues, v => (_.isArray(v) && !_.isEmpty(v)) || v);

    // for V4, only show images in this page, as only images have performance data
    filterValues.assetTypeFilter = FolderAssetType.Image;
    // to show only assets with perf data, that is, PerformanceMetrics/Impressions gt 0
    filterValues.hasPerfData = true;
    if (effectiveFilters.dateAddedDateRange && filterValues.dateAddedDateRange) {
      isDateChanged = !civilDateRangeEqual(effectiveFilters.dateAddedDateRange, filterValues.dateAddedDateRange);
    }
    if (!_.isEqual(effectiveFilters, filterValues) || isDateChanged) {
      setEffectiveFilters(filterValues);
    }
  }, [currentFolderId, effectiveFilters, filters, i18n, ianaTimeZone]);

  // ==
  // == *comment out as currently no way to switch to subfolder in performance page, uncomment if needed in the future
  // ==
  // clear search location filter when navigating to a subfolder, search location filter is only applicable in root folder
  // useEffect(() => {
  //   if (currentFolderId !== ROOT_FOLDER_ID) {
  //     const filter = _.find(filters, { type: FilterType.SearchLocation }) || {};
  //     if (filter && filter.id) {
  //       updateFilter({ id: filter.id, value: { searchLocationFilter: null }, humanizedValue: null });
  //     }
  //   }
  // }, [currentFolderId, filters, updateFilter]);

  return {
    filterStates: filters,
    filters: effectiveFilters,
    isEditingFilter,
    renderFilterBar,
    overwriteFilters,
    isAnyFilterSet,
  };
};
